import React from 'react'
import { formatDateYYYYMMDD, formatMoney } from '../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { SourceEntity } from 'types/SourceEntity'
import { getConvertibleLoanAmount } from '../utils/convertibleUtils'
import { KVLabel } from './IssueTypeLabels'

function ConvertibleLabel({
    convertible,
    link,
    adminLink,
    blankLink = true,
}: {
    blankLink?: boolean
    convertible?:
        | (Pick<
              ConvertibleEntity,
              'currency' | 'id' | 'loanAmount' | 'noncashConsideration' | 'publicationDate' | 'convertibleName'
          > & {
              sources: Array<Pick<SourceEntity, 'sourceType' | 'publishedDate'>>
          })
        | null
} & ({ adminLink?: false; link: true } | { adminLink: true; link?: false })) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const moreInfo = []

    if (!convertible) return null

    const loanAmount = getConvertibleLoanAmount(convertible as ConvertibleEntity)

    if (loanAmount) {
        moreInfo.push(
            formatMoney(loanAmount, {
                locale,
                decimals: 0,
                currencySymbol: convertible.currency,
            }),
        )
    }

    if (convertible.publicationDate) {
        moreInfo.push(formatDateYYYYMMDD(convertible.publicationDate))
    } else if (
        convertible.sources?.length > 0 &&
        convertible.sources.some((source) => source.sourceType === 'Publication')
    ) {
        const publicationDate = convertible.sources.find((source) => source.sourceType === 'Publication')?.publishedDate
        if (publicationDate) moreInfo.push(formatDateYYYYMMDD(publicationDate))
    }

    if (convertible.noncashConsideration) {
        moreInfo.push(convertible.noncashConsideration)
    }

    let moreInfoPart = null
    if (moreInfo.length > 0) {
        moreInfoPart = `(${moreInfo.join(', ')})`
    }

    if (link || adminLink) {
        const href = link ? `/convertibles/${convertible.id}` : `/admin/convertibles/${convertible.id}`
        return (
            <span>
                <Link to={href} target={blankLink ? '_blank' : '_self'}>
                    {convertible.convertibleName}
                </Link>{' '}
                {moreInfoPart}
            </span>
        )
    } else {
        return (
            <>
                {convertible.convertibleName} {moreInfoPart}
            </>
        )
    }
}

const ConvertibleTypeLabels = ({
    convertible,
    isClickable,
    noTooltip = false,
}: {
    convertible: Pick<ConvertibleEntity, 'id'>
    isClickable?: boolean
    noTooltip?: boolean
}) => {
    const handleClickConvertible = () => {
        const path = `/convertibles/${convertible.id}`

        window.open(path, '_blank')
    }

    return <KVLabel onClick={isClickable ? handleClickConvertible : undefined} noTooltip={noTooltip} />
}

export { ConvertibleLabel, ConvertibleTypeLabels }
